'use client';

import { c as _c } from "react/compiler-runtime";
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from 'cn';
import { CheckIcon, SearchIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InputGroup, InputGroupAddon } from '@/components/ui/input-group';
type CommandProps = React.ComponentProps<typeof CommandPrimitive>;
export function Command(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex size-full flex-col overflow-hidden rounded-4xl bg-popover p-1 text-popover-foreground", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive data-slot="command" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type CommandDialogProps = {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Dialog>, 'children'>;
export function CommandDialog(props) {
  const $ = _c(30);
  const t = useTranslations();
  let children;
  let className;
  let descriptionProp;
  let rest;
  let t0;
  let titleProp;
  if ($[0] !== props) {
    ({
      title: titleProp,
      description: descriptionProp,
      children,
      className,
      showCloseButton: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = descriptionProp;
    $[4] = rest;
    $[5] = t0;
    $[6] = titleProp;
  } else {
    children = $[1];
    className = $[2];
    descriptionProp = $[3];
    rest = $[4];
    t0 = $[5];
    titleProp = $[6];
  }
  const showCloseButton = t0 === undefined ? false : t0;
  let t1;
  if ($[7] !== t || $[8] !== titleProp) {
    t1 = titleProp ?? t("command-menu.title");
    $[7] = t;
    $[8] = titleProp;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const title = t1;
  let t2;
  if ($[10] !== descriptionProp || $[11] !== t) {
    t2 = descriptionProp ?? t("command-menu.description");
    $[10] = descriptionProp;
    $[11] = t;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const description = t2;
  let t3;
  if ($[13] !== className) {
    t3 = cn("top-1/3 translate-y-0 overflow-hidden rounded-4xl! p-0", className);
    $[13] = className;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== title) {
    t4 = <DialogTitle>{title}</DialogTitle>;
    $[15] = title;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== description) {
    t5 = <DialogDescription>{description}</DialogDescription>;
    $[17] = description;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <DialogHeader className="sr-only">{t4}{t5}</DialogHeader>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== children || $[23] !== showCloseButton || $[24] !== t3 || $[25] !== t6) {
    t7 = <DialogContent className={t3} showCloseButton={showCloseButton}>{t6}{children}</DialogContent>;
    $[22] = children;
    $[23] = showCloseButton;
    $[24] = t3;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== rest || $[28] !== t7) {
    t8 = <Dialog {...rest}>{t7}</Dialog>;
    $[27] = rest;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
type CommandInputProps = React.ComponentProps<typeof CommandPrimitive.Input>;
export function CommandInput(props) {
  const $ = _c(11);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("w-full text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive.Input data-slot="command-input" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <InputGroupAddon><SearchIcon className="size-4 shrink-0 opacity-50" /></InputGroupAddon>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t1) {
    t3 = <div data-slot="command-input-wrapper" className="p-1 pb-0"><InputGroup className="h-9 bg-input/30">{t1}{t2}</InputGroup></div>;
    $[9] = t1;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
type CommandListProps = React.ComponentProps<typeof CommandPrimitive.List>;
export function CommandList(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("no-scrollbar max-h-72 scroll-py-1 overflow-x-hidden overflow-y-auto outline-none", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive.List data-slot="command-list" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type CommandEmptyProps = React.ComponentProps<typeof CommandPrimitive.Empty>;
export function CommandEmpty(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("py-6 text-center text-sm", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive.Empty data-slot="command-empty" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type CommandGroupProps = React.ComponentProps<typeof CommandPrimitive.Group>;
export function CommandGroup(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("overflow-hidden p-1 text-foreground", "**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:py-2 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive.Group data-slot="command-group" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type CommandSeparatorProps = React.ComponentProps<typeof CommandPrimitive.Separator>;
export function CommandSeparator(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("my-1 h-px bg-border/50", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <CommandPrimitive.Separator data-slot="command-separator" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type CommandItemProps = React.ComponentProps<typeof CommandPrimitive.Item>;
export function CommandItem(props) {
  const $ = _c(11);
  let children;
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  let t0;
  if ($[4] !== className) {
    t0 = cn("group/command-item relative flex cursor-default items-center gap-2 rounded-lg px-3 py-2 text-sm outline-hidden select-none", "in-data-[slot=dialog-content]:rounded-2xl", "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50", "data-selected:bg-muted data-selected:text-foreground", "[&_svg]:pointer-events-none [&_svg]:shrink-0", "[&_svg:not([class*='size-'])]:size-4", "data-selected:*:[svg]:text-foreground", className);
    $[4] = className;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CheckIcon className="ml-auto opacity-0 group-has-data-[slot=command-shortcut]/command-item:hidden group-data-[checked=true]/command-item:opacity-100" />;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== rest || $[9] !== t0) {
    t2 = <CommandPrimitive.Item data-slot="command-item" className={t0} {...rest}>{children}{t1}</CommandPrimitive.Item>;
    $[7] = children;
    $[8] = rest;
    $[9] = t0;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type CommandShortcutProps = React.ComponentProps<'span'>;
export function CommandShortcut(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("ml-auto text-xs tracking-widest text-muted-foreground group-data-selected/command-item:text-foreground", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <span data-slot="command-shortcut" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}